<template>
	<b-modal
		size="xmd"
		ref="task-details-modal"
		@hidden="$emit('close')"
		:title="`${task.title}`"
		centered
		hide-footer
		no-close-on-backdrop
		scrollable
	>
		<div class="row">
			<div class="col col-8">
				<div class="m-1">
					<strong class="mr-1">Estado:</strong>
					<b-badge :variant="task.status == 'EJECUTADA' ? 'success' : 'warning'">
						{{ task.status }}
					</b-badge>
				</div>
				<div class="m-1" v-if="task.status == 'EJECUTADA'">
					<strong class="mr-1">Ejecutada por:</strong>
					{{ task.executed_by }} -
					{{ task.executed_at | myGlobalDayWhour }}
				</div>
				<div class="m-1">
					<strong class="mr-1">Fecha:</strong>
					{{ task.start | myGlobalDayWhour }}
				</div>
				<div class="m-1">
					<strong class="mr-1">Producto:</strong>
					{{ task.product_name }}
				</div>
				<div class="m-1">
					<strong class="mr-1">Observación:</strong>
					{{ task.observation }}
				</div>
			</div>
			<div class="col col-4 d-flex justify-content-end align-items-center">
				<b-button
					v-if="!isTaskExecuted && !isFutureTask"
					variant="success"
					@click="executeTask"
					:disabled="!isAllCompleted"
				>
					<FeatherIcon icon="CheckCircleIcon" class="mr-1" />
					Marcar como completada
				</b-button>
			</div>
		</div>
		<b-table
			ref="task-details-table"
			id="task-details-table"
			sticky-header="60vh"
			show-empty
			:busy.sync="isBusy"
			responsive="sm"
			:items="locations"
			:fields="fields"
		>
			<template #table-busy>
				<div class="text-center text-primary my-2">
					<b-spinner class="align-middle mr-1" />
					<strong>Loading ...</strong>
				</div>
			</template>
			<template #cell(location)="{ item }">
				{{ isCorralVisible ? item.corral_name : item.criadora_name }}
			</template>
			<template #cell(check)="row">
				<FeatherIcon
					@click="
						!isTaskExecuted && !isFutureTask
							? executeTaskPartially(row.item.details, !row.item.isCompleted)
							: null
					"
					icon="CheckCircleIcon"
					size="20"
					:class="{
						'text-success': row.item.isCompleted,
						'text-secondary': !row.item.isCompleted,
						clickable: !isTaskExecuted && !isFutureTask,
					}"
				/>
			</template>
			<template #cell(details)="row">
				<div @click="row.toggleDetails" class="cursor-pointer">
					<b-badge variant="primary" style="width: 50px">
						<feather-icon
							class="text-white"
							:icon="row.detailsShowing ? 'ChevronUpIcon' : 'ChevronDownIcon'"
							size="21"
						/>
					</b-badge>
				</div>
			</template>
			<template #row-details="row">
				<b-card class="m-0 p-0 custom-card">
					<b-table-simple sticky-header="60vh">
						<b-thead class="text-center">
							<b-tr>
								<b-th>Postura</b-th>
								<b-th>Madrilla</b-th>
								<b-th>Padrillo</b-th>
								<b-th v-if="isCorralVisible">Cintillos</b-th>
								<template v-else>
									<b-th>Pata Izq.</b-th>
									<b-th>Pata Der.</b-th>
								</template>
							</b-tr>
						</b-thead>
						<b-tbody class="text-center">
							<b-tr v-for="(detail, index) in row.item.details" :key="index">
								<b-td>
									{{ detail.postura_code }}
								</b-td>
								<b-td>{{ detail.madrilla_alias }} / {{ detail.madrilla_plate }}</b-td>
								<b-td>{{ detail.padrillo_alias }} / {{ detail.padrillo_plate }}</b-td>
								<b-td v-if="isCorralVisible">
									<b-badge
										v-for="(correlative, index) in detail.correlatives"
										:key="`correlative-${index}`"
										variant="primary"
										style="margin: 5px"
									>
										{{ correlative }}
									</b-badge>
								</b-td>
								<template v-else>
									<b-td>
										<CombChickenlegsCintillos
											:postura="{
												id: detail.id,
												chickenleg_wich_leg: detail.cintillos.chickenleg_wich_leg,
												chickenleg_qty_cintillos: detail.cintillos.qty_cintillos,
												cintillo_color: detail.cintillos.hex_1,
												cintillo_description: detail.cintillos.description_1,
												second_color_color: detail.cintillos.hex_2,
												second_color_description: detail.cintillos.description_2,
												total_minus_infertile_eggs: 1,
											}"
											:chickenleg_wich_leg="1"
										/>
									</b-td>
									<b-td>
										<CombChickenlegsCintillos
											:postura="{
												id: detail.id,
												chickenleg_wich_leg: detail.cintillos.chickenleg_wich_leg,
												chickenleg_qty_cintillos: detail.cintillos.qty_cintillos,
												cintillo_color: detail.cintillos.hex_1,
												cintillo_description: detail.cintillos.description_1,
												second_color_color: detail.cintillos.hex_2,
												second_color_description: detail.cintillos.description_2,
												total_minus_infertile_eggs: 1,
											}"
											:chickenleg_wich_leg="2"
										/>
									</b-td>
								</template>
							</b-tr>
							<b-tr v-if="row.item.details.length == 0">
								<b-td colspan="4">
									<p class="mb-0">No hay registros por mostrar</p>
								</b-td>
							</b-tr>
						</b-tbody>
					</b-table-simple>
				</b-card>
			</template>
		</b-table>
	</b-modal>
</template>

<script>
import TaskCalendarService from "@/services/encaste/task-calendar/task-calendar.service.js"
import FeatherIcon from "@/@core/components/feather-icon/FeatherIcon.vue"
import CombChickenlegsCintillos from "@/views/amg/encaste/lotes/components/CombChickenlegsCintillos.vue"
import { mapActions } from "vuex"
export default {
	name: "TaskDetailsModal",
	props: {
		task: Object,
	},
	components: { FeatherIcon, CombChickenlegsCintillos },
	async mounted() {
		await this.getDetails()
		this.toggleModal("task-details-modal")
	},
	computed: {
		isFutureTask() {
			const today = new Date()
			const formattedDate = today.toLocaleDateString("en-CA")
			return this.task.start > formattedDate
		},
		isCorralVisible() {
			if (this.task.status == "EJECUTADA") {
				return this.task.executed_in == "CORRAL" ? true : false
			} else {
				return this.has_corral
			}
		},
		isTaskExecuted() {
			return this.task.status == "EJECUTADA"
		},
		isAllCompleted() {
			return this.rows.every((row) => row.is_completed)
		},
	},
	data() {
		return {
			rows: [],
			has_corral: false,
			locations: [],
			selectedKey: null,
			isBusy: false,
			fields: [
				{
					key: "location",
					label: "Ubicación",
					visible: true,
					thClass: "text-center",
					tdClass: "text-center",
				},
				{
					key: "check",
					label: "Completado",
					visible: true,
					thClass: "text-center",
					tdClass: "text-center",
				},
				{
					key: "details",
					label: "Ver detalles",
					visible: true,
					thClass: "text-center",
					tdClass: "text-center",
				},
			],
		}
	},

	methods: {
		async executeTaskPartially(details, is_completed) {
			const egg_lote_ids = details.map((detail) => detail.egg_lote_id)
			this.isPreloading()
			try {
				const params = {
					task_id: this.task.id,
					egg_lote_ids,
					is_completed,
				}
				await TaskCalendarService.executeTaskPartially(params)
				this.getDetails()
			} catch (error) {
				console.error(error)
			} finally {
				this.isPreloading(false)
			}
		},
		...mapActions({
			A_GET_ENCASTE_TASK_COUNTER: "commons/A_GET_ENCASTE_TASK_COUNTER",
		}),
		async getDetails() {
			this.isPreloading()
			try {
				const { data } = await TaskCalendarService.getTaskDetail(this.task.id)
				this.rows = data
				this.rows = this.rows.map((item) => {
					let cintillos = {
						...item.cintillos,
						chickenleg_wich_leg:
							item.cintillos &&
							(item.cintillos.wich_leg == "IZQUIERDA" ? 1 : item.cintillos.wich_leg == "DERECHA" ? 2 : 3),
					}
					return {
						...item,
						cintillos: item.cintillos ? cintillos : null,
					}
				})
				this.has_corral = this.rows.some((row) => row.corral_name)
				if (this.has_corral) {
					this.rows = this.rows.filter((row) => row.correlatives)
				}
				this.selectedKey = this.isCorralVisible ? "corral_id" : "criadora_id"
				this.locations = this.uniqueArray(this.rows)
				this.locations = this.locations.map((l) => {
					const details = this.rows.filter((r) => r[this.selectedKey] === l[this.selectedKey])
					return {
						...l,
						isCompleted: details.every((detail) => detail.is_completed),
						detailsShowing: false,
						details,
					}
				})
			} catch (error) {
				console.error(error)
			} finally {
				this.isPreloading(false)
			}
		},
		uniqueArray(data) {
			const seen = new Set()
			return data.filter((item) => {
				const duplicate = seen.has(item[this.selectedKey])
				seen.add(item[this.selectedKey])
				return !duplicate
			})
		},
		toggleModal(modalRef) {
			this.$refs[modalRef].toggle()
		},
		async executeTask() {
			this.isPreloading()
			try {
				const params = {
					has_corral: this.has_corral,
				}
				const { message } = await TaskCalendarService.executeTask(this.task.id, params)
				this.showSuccessToast(message)
				this.A_GET_ENCASTE_TASK_COUNTER()
				this.$emit("refreshCalendar")
				this.$emit("close")
			} catch (error) {
				console.error(error)
			} finally {
				this.isPreloading(false)
			}
		},
	},
}
</script>

<style lang="css" scoped>
.custom-card {
	border: 3px solid var(--primary);
	border-radius: 10px;
	padding: 1rem;
}
</style>
